import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AmlHistoryItemData } from '@/redux/models';
import { getRiskByGroup, getRiskGroups } from '@/helpers';
import { CounterpartyConnections } from '../CounterpartyConnections';
import { CounterpartySignals } from '../CounterpartySignals';
import { SideEnum } from '@/api';
import {
  InputSwitch,
  RiskDetailsCategory,
  RiskDetailsInfo,
} from '@/components';
import styles from './TransactionSignals.module.sass';

interface TransactionSignalsProps {
  item: AmlHistoryItemData;
}

const tabOriginKey = 'address';
const tabCounterpartyKey = 'cp';

const TransactionSignals = ({ item }: TransactionSignalsProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(tabOriginKey);
  const groups = getRiskGroups(item.signals, item.flow);
  const hasSignals = useMemo(() => {
    return Object.keys(groups).length > 0;
  }, [groups]);

  const mainTabTitle = useMemo(() => {
    return item.direction === SideEnum.DEPOSIT
      ? t('translation.tx_risk_deposit_title')
      : t('translation.tx_risk_withdraw_title');
  }, [item, t]);

  const signalTabTitles = useMemo(() => {
    return [
      { label: mainTabTitle, value: tabOriginKey },
      {
        label:
          item?.direction === SideEnum.DEPOSIT
            ? t('translation.tx_cp_deposit_title')
            : t('translation.tx_cp_withdraw_title'),
        value: tabCounterpartyKey,
      },
    ];
  }, [item?.direction, mainTabTitle, t]);

  const groupFigures = useMemo(() => getRiskByGroup(groups), [groups]);

  const showCounterpartyData = useMemo(() => {
    return !!item.counterparty && !item.counterparty?.name;
  }, [item]);

  const hasCounterpartySignals = useMemo(() => {
    if (!item.counterparty?.signals) {
      return false;
    }

    const incomingPercent: number = (
      Object.values(item.counterparty?.signals.in) as number[]
    ).reduce((acc, val) => acc + val, 0);

    if (incomingPercent > 0) return true;

    const outgoingPercent: number = (
      Object.values(item.counterparty?.signals.out) as number[]
    ).reduce((acc, val) => acc + val, 0);

    return outgoingPercent > 0;
  }, [item]);

  return (
    <>
      {(!showCounterpartyData || !hasCounterpartySignals) && (
        <RiskDetailsInfo item={item} />
      )}

      {showCounterpartyData && hasCounterpartySignals && (
        <>
          <div className={styles.direction}>
            <InputSwitch
              options={signalTabTitles}
              name="direction"
              onChange={(val) => setActiveTab(val)}
            />
          </div>

          <div className={styles.details__list}>
            <div className={styles.signals}>
              {activeTab === tabOriginKey && hasSignals && (
                <>
                  <div className={styles.list}>
                    {Object.keys(groups).map((category) => (
                      <div key={category}>
                        <RiskDetailsCategory
                          category={category}
                          risks={groups[category]}
                          percentage={groupFigures[category]}
                        />
                      </div>
                    ))}
                  </div>

                  <CounterpartyConnections
                    connections={item.connections}
                    tooltip={t(
                      item.direction === SideEnum.DEPOSIT
                        ? 'translation.entities_tooltip_tx_deposit_source'
                        : 'translation.entities_tooltip_tx_withdrawal_dest',
                    )}
                  />
                </>
              )}
            </div>

            {activeTab === tabCounterpartyKey && (
              <div className={styles.signals}>
                {hasCounterpartySignals && (
                  <CounterpartySignals
                    flow={item.flow}
                    counterparty={item.counterparty}
                  />
                )}
                <CounterpartyConnections
                  connections={item.counterparty?.connections}
                  tooltip={t(
                    item.direction === SideEnum.DEPOSIT
                      ? 'translation.entities_tooltip_tx_deposit_sender'
                      : 'translation.entities_tooltip_tx_withdrawal_receiver',
                  )}
                />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default TransactionSignals;
